/* eslint-disable @angular-eslint/prefer-inject */
import { moveItemInArray } from '@angular/cdk/drag-drop';
import {
    isTypeExcludedFromOperations,
    SlotCreate,
    SlotDto,
    SlotRead,
    SlotSearchParams,
} from '../entities/slot.dto';
import { SlotStore } from '../infrastructure/slots/slot.store';
import { inject, Injectable } from '@angular/core';
import { AbstractManageEntityFacade } from '@pf/shared-services';
import { SlotMapper } from '../infrastructure/slots/slot.mapper';
import { BuildChangeTrackingFacadeFactory } from '@pf/shared/util-platform';
import {
    ColumnSearchType,
    EntitySaveOptions,
    IBarcodeSearchFacade,
    IColumnSearchFacade,
    IManageEntityRoutes,
    IPagedResult,
    IQueryParams,
    QueryFilters,
    SafeAny,
    SelectOption,
} from '@pf/shared-common';
import {
    combineLatest,
    forkJoin,
    map,
    Observable,
    of,
    switchMap,
    tap,
} from 'rxjs';
import {
    ItemFragment,
    ResourceAuth,
    SlotPriorityUpdate,
    SlotType,
    WarehouseEquipmentCategory,
    ZoneType,
} from '@control-tower/platform-wms';
import { StatusOptions } from '@pf/shared-ui';
import { WarehouseDataService } from '../infrastructure/warehouses/warehouse.data.service';
import { SlotDataService } from '../infrastructure/slots/slot.data.service';
import { ZoneDto } from '../entities/zone.dto';
import {
    asNumberSafe,
    compareSafe,
    flattenAndMapToSelectOptions,
} from '@pf/shared-utility';
import { ZoneSlotService } from './zone-slot.service';
import { ContainerFacade } from './container.facade';
import { ContainerDto } from '../entities/container.dto';
import { WarehouseStore } from '../infrastructure/warehouses/warehouse.store';
import { WarehouseFacade } from './warehouse.facade';
import { locationIdentifiersKey } from './zone.facade';
import { InventoryBundleFacade } from './inventory-bundle.facade';
import { uniqBy } from 'lodash-es';

@Injectable()
export class SlotFacade
    extends AbstractManageEntityFacade<
        SlotDto,
        SlotRead,
        SlotCreate,
        SlotSearchParams
    >
    implements IColumnSearchFacade<SlotDto>, IBarcodeSearchFacade<SlotDto>
{
    private readonly _containerFacade = inject(ContainerFacade);
    private readonly _warehouseStore = inject(WarehouseStore);
    private readonly _warehouseDataService = inject(WarehouseDataService);
    private readonly _zoneSlotService = inject(ZoneSlotService);
    private readonly _warehouseFacade = inject(WarehouseFacade);
    private readonly _inventoryBundleFacade = inject(InventoryBundleFacade);

    nameField: keyof SlotDto = 'barcode';

    slotTypes: StatusOptions = {
        [SlotType.Primary]: {
            label: 'Primary',
            color: '#0000FF',
        },
        [SlotType.Secondary]: {
            label: 'Secondary',
            color: '#800080',
        },
        [SlotType.Overflow]: {
            label: 'Overflow',
            color: '#FFA500',
        },
        [SlotType.Staging]: {
            label: 'Staging',
            color: '#808080',
        },
        [SlotType.Quarantine]: {
            label: 'Quarantine',
            color: '#f65c43',
        },
        [SlotType.NonCompliance]: {
            label: 'Non-Compliance',
            color: '#dd9f51',
        },
        [SlotType.Recovery]: {
            label: 'Recovery',
            color: '#228B22',
        },
        defaultOption: {
            status: 'danger',
            label: 'Unknown',
        },
    };

    constructor(
        dataService: SlotDataService,
        store: SlotStore,
        mapper: SlotMapper
    ) {
        super(
            dataService,
            null as unknown as IManageEntityRoutes,
            store,
            mapper
        );
    }

    get columnListFilterName(): string {
        return 'slot';
    }

    override mapToSelectOptions(
        source: Observable<IPagedResult<SlotDto>>
    ): Observable<SelectOption[]> {
        return flattenAndMapToSelectOptions<SlotDto>(
            slot => `[${slot.slotType}] ${slot.barcode}`,
            'id',
            slot => [
                slot.zone.locationIdentifiers?.length
                    ? locationIdentifiersKey(slot.zone.locationIdentifiers)
                    : ((slot.zone.name || slot.zone.barcode) as string),
            ]
        )(source);
    }

    getColumnListFilters$(
        additionalFilters?: QueryFilters<SlotDto>
    ): Observable<ColumnSearchType[]> {
        const queryFilters = {
            pageIndex: 1,
            pageSize: 50,
            filters: additionalFilters,
        };

        return this.searchAll$(queryFilters, false).pipe(
            map(result =>
                result.map(
                    slot =>
                        ({
                            value: slot.id,
                            label: slot.barcode as string,
                        }) as ColumnSearchType
                )
            )
        );
    }

    searchByBarcode$(barcodeText: string): Observable<SlotDto | null> {
        if (!barcodeText) {
            return of(null);
        }

        return this.search$({
            pageIndex: 1,
            pageSize: 1,
            filters: {
                barcode: barcodeText,
            },
        }).pipe(
            map(
                results =>
                    results.data.find(slot =>
                        compareSafe(slot.barcode, barcodeText)
                    ) ?? null
            )
        );
    }

    override getDefaultSearchParams$(
        params: IQueryParams<SlotDto>
    ): Observable<IQueryParams<SlotDto>> {
        return this._warehouseFacade.addDefaultWarehouseParam(params);
    }

    override add() {
        this.store.resetActiveEntity();
    }

    override manage() {
        this.store.resetActiveEntity();
    }

    override edit(entityId: string): void {
        this.store.setActiveEntity(entityId);
    }

    override save$(
        dto: SlotDto,
        options?: EntitySaveOptions<SlotDto>
    ): Observable<SlotDto> {
        return of(dto).pipe(
            map(slot => {
                slot.barcode =
                    slot.barcode ||
                    this._zoneSlotService.getZoneSlotBarcode(slot.zoneId);
                slot.slotPriority =
                    slot.slotPriority ||
                    this._zoneSlotService.getNextPriority(slot.zoneId);
                slot.excludeFromOperations =
                    slot.excludeFromOperations ||
                    isTypeExcludedFromOperations(slot.slotType);

                return slot;
            }),
            switchMap(slot => {
                return super.save$(slot, options);
            }),
            switchMap(slot => {
                return this.ensureDefaultContainer$(slot);
            })
        );
    }

    changeTrackingFactory = BuildChangeTrackingFacadeFactory(
        this.dataService,
        this.nameField as SafeAny
    );

    textSearchFilter(
        searchText: string
    ): Partial<Record<keyof SlotDto, string>> {
        return { barcode: searchText };
    }

    updateSlotPriority(zone: ZoneDto, fromIndex: number, toIndex: number) {
        const slots = this.getZoneSlots(zone.id);
        moveItemInArray(slots, fromIndex, toIndex);

        const updatedSlots = slots.map((slot, index) => {
            return {
                ...slot,
                slotPriority: index + 1,
            };
        });
        const slotPriorityUpdates = updatedSlots.map(slot => {
            return {
                id: slot.id,
                priority: slot.slotPriority,
            } as SlotPriorityUpdate;
        });

        return this._warehouseDataService
            .updateStructure$(zone.warehouseId, [], slotPriorityUpdates)
            .pipe(
                tap(() => {
                    this.store.upsertMany(updatedSlots);
                    this.store.setPageByIds(
                        1,
                        updatedSlots.map(s => s.id)
                    );
                })
            );
    }

    private getZoneSlots(zoneId: string) {
        return this.store
            .filter((s: SlotRead) => !s.isDeleted && s.zone?.id === zoneId)
            .sort(
                (a, b) =>
                    asNumberSafe(a.slotPriority) - asNumberSafe(b.slotPriority)
            );
    }

    override applyResourceAuth(body: SlotCreate): void {
        const locationId = this._warehouseFacade.active?.location?.id;
        body.resourceAuth = {
            locations: locationId ? [locationId] : [],
        } as ResourceAuth;
    }

    bulkAdd$(quantity: number, slotTemplate: SlotDto) {
        const zoneId = (slotTemplate.zoneId || slotTemplate.zone.id) as string;
        const nextPriority = this._zoneSlotService.getNextPriority(zoneId);
        return of(this._zoneSlotService.getBulkBarcodes(zoneId, quantity)).pipe(
            map(barcodes =>
                barcodes.map((barcode, index) => {
                    return new SlotDto({
                        ...slotTemplate,
                        barcode,
                        slotPriority: nextPriority + index,
                    });
                })
            ),
            switchMap(slots => {
                return combineLatest(
                    slots.map(slot => this.save$(slot, { emitEvent: false }))
                );
            }),
            tap(() => {
                this.store.setPageByIds(
                    1,
                    this.getZoneSlots(zoneId).map(s => s.id)
                );
            })
        );
    }

    private ensureDefaultContainer$(slot: SlotDto): Observable<SlotDto> {
        const equipmentTypeCategory = slot.equipmentType?.category;

        if (!equipmentTypeCategory) {
            throw new Error('Equipment type is required for slots');
        }

        if (
            equipmentTypeCategory === WarehouseEquipmentCategory.Forklift ||
            equipmentTypeCategory === WarehouseEquipmentCategory.PalletJack
        ) {
            return of(slot);
        }
        if (slot.containers.length === 0) {
            return this._containerFacade
                .save$(this.createSlotContainer(slot))
                .pipe(
                    map(container => {
                        slot.containers = [...slot.containers, container];
                        return slot;
                    })
                );
        }

        return of(slot);
    }

    private createSlotContainer(slot: SlotDto) {
        return new ContainerDto({
            lpn: slot.barcode,
            slot: { id: slot.id },
            warehouse: { id: this._warehouseStore.active?.id },
        });
    }

    getFirstEmptySlotByTypeAndZoneType$(
        slotType: SlotType,
        zoneType: ZoneType = ZoneType.Packing
    ): Observable<SlotDto | null> {
        return this.oneTimeSearch$({
            pageIndex: 1,
            pageSize: 1,
            filters: {
                slotType,
                zoneType,
                hasContainers: false,
            } as SafeAny,
        }).pipe(
            map(results => (results.data?.length ? results.data[0] : null))
        );
    }

    getItemsForSlotIds$(slotIds: string[]): Observable<ItemFragment[]> {
        return forkJoin([
            this.searchAll$(
                {
                    pageIndex: 1,
                    pageSize: 50,
                    filters: {
                        ids: slotIds,
                    } as SafeAny,
                },
                false
            ),
            this._inventoryBundleFacade.getAllBySlotIds$(slotIds),
        ]).pipe(
            map(([slots, slotBundles]) => {
                const reservedItems = slots
                    .flatMap(slot => slot.reservedForItem)
                    .filter(Boolean) as ItemFragment[];
                const bundleItems = slotBundles
                    .map(b => b.item)
                    .filter(Boolean) as ItemFragment[];

                return uniqBy(
                    [...reservedItems, ...bundleItems],
                    item => item.id
                );
            })
        );
    }
}
